import { useMemo } from "react";
import { MultiSelectDropdown } from "components/common/searchableDropdown/MultiSelectDropdown";
import { SearchableDropdown } from "components/common/searchableDropdown/SearchableDropdown";
import { InputV3 } from "components/common/input/InputV3";
import type { TableColumnFilterProps } from "./TableColumnFilter.types";

export const TableColumnFilter = ({
  config,
  value,
  onChange,
  accent = "gray",
}: TableColumnFilterProps) => {
  const dropdownOptions = useMemo(() => {
    if (!config.options) return [];
    return config.options.map((opt) => ({
      value: String(opt.value),
      label: opt.label,
    }));
  }, [config.options]);

  const handleMultiSelectChange = (values: (string | number)[]) => {
    const stringValues = values.map((v) => String(v));
    onChange(stringValues.length > 0 ? stringValues : undefined);
  };

  const handleSingleSelectChange = (selectedValue: string) => {
    onChange(selectedValue || undefined);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    // Store the full value (including spaces) so user can type normally
    // Filtering logic will handle trimming
    onChange(searchValue || undefined);
  };

  if (config.type === "multiselect") {
    return (
      <MultiSelectDropdown
        name={`filter-${config.columnKey}`}
        options={dropdownOptions}
        placeholder={config.placeholder || "All"}
        value={Array.isArray(value) ? value : []}
        onChange={handleMultiSelectChange}
        noFormContext
        triggerClassName="w-full"
      />
    );
  }

  if (config.type === "select") {
    return (
      <SearchableDropdown
        name={`filter-${config.columnKey}`}
        options={dropdownOptions}
        placeholder={config.placeholder || "All"}
        value={typeof value === "string" ? value : ""}
        onChange={handleSingleSelectChange}
        noFormContext
        variant="button"
        color={accent}
        triggerClassName="w-full"
      />
    );
  }

  if (config.type === "search") {
    return (
      <InputV3
        type="search"
        placeholder={config.placeholder || "Search..."}
        value={typeof value === "string" ? value : ""}
        onChange={handleSearchChange}
        className="bg-muted/40 border-border text-sm h-8"
      />
    );
  }

  return null;
};
